import {
  Box,
  Chip,
  FormHelperText,
  InputLabel,
  Typography,
} from "@mui/material";

import { LockIcon } from "lucide-react";
import type { ReactNode } from "react";
import {
  type Control,
  type FieldError,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  getAdministrativeUserRoles,
  getOperativeRoles,
  getServiceRoles,
  getZoneUserRolesAsEntity,
  ZoneUserRole,
} from "constants/userContstants";
import { IRole } from "interfaces/user.interface";

import { RoleInformation } from "./RoleInformation";

type RoleChipProps = {
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  label: string;
};

function RoleChip({ selected, disabled, onClick, label }: RoleChipProps) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      onDelete={selected ? onClick : undefined}
      disabled={disabled}
      icon={disabled ? <LockIcon /> : undefined}
      sx={(theme) => ({
        backgroundColor: selected
          ? theme.vars.palette.backgrounds.success.soft.default
          : theme.vars.palette.backgrounds.neutral.moderate.default,
        "&:hover": {
          backgroundColor: selected
            ? theme.vars.palette.backgrounds.success.soft.hover
            : theme.vars.palette.backgrounds.neutral.moderate.hover,
        },
      })}
    />
  );
}

type RoleInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  control: Control<T>;
  errorMap?: Partial<{ [key in FieldError["type"]]: string }>;
};

export function RoleInput<T extends FieldValues>({
  fieldName,
  control,
  label,
  errorMap = {},
}: RoleInputProps<T>) {
  const { t } = useTranslation();

  const {
    field,
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  const roles = getZoneUserRolesAsEntity(t);

  const onClick = (role: IRole) => {
    if (field.value.includes(role.value)) {
      field.onChange(field.value.filter((v: string) => v !== role.value));
    } else {
      field.onChange([
        ...field.value.filter((r: string) => roles[r]?.type !== role.type),
        role.value,
      ]);
    }
  };

  const serviceMonitorSelected = field.value.includes(
    ZoneUserRole.SERVICE_MONITOR,
  );

  const nonServiceMonitorSelected = field.value.some(
    (role: string) => role !== ZoneUserRole.SERVICE_MONITOR,
  );

  return (
    <>
      <InputLabel>{label}</InputLabel>
      {error?.type && errorMap[error.type] && (
        <FormHelperText>{errorMap[error.type]}</FormHelperText>
      )}
      <Box>
        <Typography variant={"bodySmallRegular"} gutterBottom>
          {t("administrative_roles")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {getAdministrativeUserRoles(t).map((role) => (
            <RoleChip
              key={role.name}
              selected={field.value.includes(role.value)}
              label={role.name}
              onClick={() => onClick(role)}
              disabled={serviceMonitorSelected}
            />
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant={"bodySmallRegular"} gutterBottom>
          {t("operative_roles")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {getOperativeRoles(t).map((role) => (
            <RoleChip
              key={role.name}
              selected={field.value.includes(role.value)}
              label={role.name}
              onClick={() => onClick(role)}
              disabled={serviceMonitorSelected}
            />
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant={"bodySmallRegular"} gutterBottom>
          {t("service_account_role_category")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {getServiceRoles(t).map((role) => (
            <RoleChip
              key={role.name}
              selected={field.value.includes(role.value)}
              label={role.name}
              onClick={() => onClick(role)}
              disabled={nonServiceMonitorSelected}
            />
          ))}
        </Box>
      </Box>
      <Box>
        <Typography gutterBottom variant={"bodySmallRegular"}>
          {t("the_selected_role_allows_the_user_to")}
        </Typography>
        <RoleInformation roles={field.value} />
      </Box>
    </>
  );
}
